import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons"
import { Alert, Button, Flex, Form, Grid, Modal, Steps, Typography } from "antd"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { isRowCommitEnter } from "../../../../components/EntityTable/editable-row-cells.ts"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch.ts"
import { useIdempotencyKey } from "../../../../hooks/use-idempotency-key.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import { RoutesPaths } from "../../../../constants/routes.ts"
import {
  createProject,
  updateProjectTypeToProject
} from "../../redux/project.thunks.ts"
import { projectTypeCategoryUpdates } from "../../helpers/project-type-category-updates.ts"
import { ProjectWizardCustomerStep } from "./ProjectWizardCustomerStep.tsx"
import { ProjectWizardDetailsStep } from "./ProjectWizardDetailsStep.tsx"
import { ProjectWizardAbandonConfirm } from "./ProjectWizardAbandonConfirm.tsx"
import { createProjectWizardErrorMessage } from "./create-project-wizard-error.ts"
import { buildCreateProjectWizardPayload } from "./create-project-wizard-payload.ts"
import type { ProjectWizardFormData } from "./project-wizard-data.ts"

const CUSTOMER_STEP = 0
const PROJECT_STEP = 1

const MODAL_WIDTH = 800

interface CreateProjectWizardProps {
  onClose: () => void
}

export function CreateProjectWizard({ onClose }: CreateProjectWizardProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { normalizeDate } = useDateFormat()
  const { key: idempotencyKey, reset: resetIdempotencyKey } =
    useIdempotencyKey()
  const [form] = Form.useForm<ProjectWizardFormData>()
  const screens = Grid.useBreakpoint()

  const saving = useAppSelector((state) => state.project.saveFetching)

  const [step, setStep] = useState(CUSTOMER_STEP)
  const [error, setError] = useState<string | null>(null)
  const [abandoning, setAbandoning] = useState(false)

  const fullScreen = !screens.md

  const values = Form.useWatch([], form)
  const hasEnteredData = useMemo(() => {
    if (!values) return false

    return Object.entries(values).some(([key, value]) => {
      if (key === "source") return false
      if (Array.isArray(value)) return value.length > 0
      if (value && typeof value === "object") {
        return Object.values(value).some((nested) => !!nested)
      }
      return !!value
    })
  }, [values])

  const requestClose = () => {
    if (!hasEnteredData) {
      onClose()
      return
    }
    setAbandoning(true)
  }

  const discard = () => {
    setAbandoning(false)
    form.resetFields()
    onClose()
  }

  const onSubmit = async (submitted: ProjectWizardFormData) => {
    setError(null)

    try {
      const project = await dispatch(
        createProject({
          ...buildCreateProjectWizardPayload({
            ...submitted,
            start_date: submitted.start_date
              ? normalizeDate(submitted.start_date)
              : null,
            end_date: submitted.end_date
              ? normalizeDate(submitted.end_date)
              : null,
            project_creation_date: submitted.project_creation_date
              ? normalizeDate(submitted.project_creation_date)
              : null
          }),
          idempotency_key: idempotencyKey
        })
      ).unwrap()

      // The attempt is over. Without this the wizard, which stays open when a
      // follow-up call below fails, would replay this project for a resubmit
      // the user had meanwhile edited.
      resetIdempotencyKey()

      for (const update of projectTypeCategoryUpdates(
        submitted.project_type,
        submitted.project_type_categories
      )) {
        await dispatch(
          updateProjectTypeToProject({ projectId: project.id, data: update })
        ).unwrap()
      }

      navigate(RoutesPaths.project.edit(project.id))
      onClose()
    } catch (rejection) {
      setError(
        createProjectWizardErrorMessage(
          rejection,
          t("projectWizard.error.fallback")
        )
      )
    }
  }

  // Standing rule: Enter applies/saves on a field-entry screen. The Save
  // action here (Create) lives on the final step alone, so Enter mirrors
  // whichever action the footer button on the current step performs —
  // advance on the customer step, submit on the last one — the same split
  // the Next/Create buttons already make. Reuses the inline-edit-row
  // predicate (button/select/date-picker excluded, textarea not) since the
  // rule is identical, just over a wizard step instead of a table row.
  const onFormKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (!isRowCommitEnter(event)) return
    // The step rail (`antd Steps`) renders each title as its own
    // `role="button"` element and — unlike a real <button> — never stops its
    // own Enter keydown from bubbling once it navigates, so pressing Enter to
    // step back through the rail would otherwise also reach here and
    // submit/advance a second time on the same keystroke.
    if ((event.target as HTMLElement)?.closest?.('[role="button"]')) return
    event.preventDefault()
    if (step === CUSTOMER_STEP) {
      setStep(PROJECT_STEP)
    } else {
      form.submit()
    }
  }

  const onSubmitFailed = ({
    errorFields
  }: {
    errorFields: { name: unknown[] }[]
  }) => {
    const firstErrored = errorFields?.[0]?.name?.[0]
    setStep(firstErrored === "customer" ? CUSTOMER_STEP : PROJECT_STEP)
  }

  const footer =
    step === CUSTOMER_STEP ? (
      <Flex gap={"small"} wrap justify={"space-between"}>
        <Button className={"min-h-11 max-sm:w-full"} onClick={requestClose}>
          {t("projectWizard.actions.cancel")}
        </Button>
        <Button
          key={"wizard-next"}
          type={"primary"}
          icon={<ArrowRightOutlined aria-hidden />}
          iconPosition={"end"}
          className={"min-h-11 max-sm:w-full"}
          onClick={() => setStep(PROJECT_STEP)}
        >
          {t("projectWizard.actions.next")}
        </Button>
      </Flex>
    ) : (
      <Flex gap={"small"} wrap justify={"space-between"}>
        <Button
          key={"wizard-back"}
          icon={<ArrowLeftOutlined aria-hidden />}
          className={"min-h-11 max-sm:w-full"}
          onClick={() => setStep(CUSTOMER_STEP)}
        >
          {t("projectWizard.actions.back")}
        </Button>
        <Flex gap={"small"} wrap className={"max-sm:w-full"}>
          <Button className={"min-h-11 max-sm:flex-1"} onClick={requestClose}>
            {t("projectWizard.actions.cancel")}
          </Button>
          <Button
            key={"wizard-create-primary"}
            type={"primary"}
            loading={saving}
            disabled={saving}
            className={"min-h-11 max-sm:flex-1"}
            onClick={() => form.submit()}
          >
            {t("projectWizard.actions.create")}
          </Button>
        </Flex>
      </Flex>
    )

  return (
    <>
      <Modal
        open
        centered={!fullScreen}
        width={fullScreen ? "100vw" : MODAL_WIDTH}
        title={
          <Flex vertical gap={2}>
            <Typography.Title level={4} className={"m-0"}>
              {t("projectWizard.title")}
            </Typography.Title>
            <Typography.Text type={"secondary"} className={"text-xs font-normal"}>
              {t("projectWizard.subtitle")}
            </Typography.Text>
          </Flex>
        }
        onCancel={requestClose}
        footer={footer}
        style={
          fullScreen
            ? { top: 0, maxWidth: "100vw", margin: 0, paddingBottom: 0 }
            : undefined
        }
        styles={{
          content: fullScreen
            ? { borderRadius: 0, minHeight: "100dvh" }
            : {
                display: "flex",
                flexDirection: "column",
                minHeight: "min(760px, calc(100dvh - 48px))",
                maxHeight: "calc(100dvh - 48px)"
              },
          body: {
            flex: 1,
            maxHeight: "calc(100dvh - 220px)",
            overflowX: "hidden",
            overflowY: "auto"
          }
        }}
      >
        <Form
          form={form}
          layout={"vertical"}
          disabled={saving}
          onFinish={onSubmit}
          onFinishFailed={onSubmitFailed}
          onKeyDown={onFormKeyDown}
          data-testid={"create-project-wizard"}
          className={"w-full with-default-error"}
        >
          <Steps
            responsive
            current={step}
            className={"mx-auto mb-8 max-w-xl"}
            onChange={setStep}
            items={[
              { title: t("projectWizard.steps.customer") },
              { title: t("projectWizard.steps.project") }
            ]}
          />

          {error && (
            <Alert
              type={"error"}
              showIcon
              closable
              className={"mb-4"}
              message={t("projectWizard.error.title")}
              description={error}
              onClose={() => setError(null)}
            />
          )}

          {/* Both steps stay mounted: an unmounted Form.Item registers no
              rules, so Create Project on step 2 would not see a missing
              customer name. */}
          <div hidden={step !== CUSTOMER_STEP}>
            <ProjectWizardCustomerStep form={form} />
          </div>
          <div hidden={step !== PROJECT_STEP}>
            <ProjectWizardDetailsStep />
          </div>
        </Form>
      </Modal>

      {abandoning && (
        <ProjectWizardAbandonConfirm
          open
          onCancel={() => setAbandoning(false)}
          onOk={discard}
        />
      )}
    </>
  )
}
